import Header from './Header';
import MobileNavigation from './MobileNavigation';
import Footer from './Footer';
import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import frontImg from './img/ecommerce-integrations.png';
import background from './img/softwarePartners.png';
import { Link } from 'react-router-dom';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const plugins = [
  {
    name: 'WooCommerce',
    slug: 'woo-commerce',
    host: 'WordPress',
    description: 'Card and ACH checkout for WordPress stores, with saved payment methods.'
  },
  {
    name: 'Gravity Forms',
    slug: 'gravity-forms',
    host: 'WordPress',
    description: 'One-time card and ACH payments taken straight from a form submission.'
  },
  {
    name: 'Adobe Commerce',
    slug: 'adobe-magento',
    host: 'Magento 2',
    description: 'Credit and debit card checkout for Adobe Commerce and Magento Open Source.'
  }
];

const capabilities = [
  {
    icon: 'tokenization',
    title: 'Secure tokenization',
    description: 'Card details are replaced with a token before they reach your server.'
  },
  {
    icon: 'simple-pay',
    title: 'Card and ACH',
    description: 'Credit, debit, and bank account payments through one gateway.'
  },
  {
    icon: 'customers',
    title: 'Saved payment methods',
    description: 'Faster repeat checkout for logged-in WooCommerce customers.'
  },
  {
    icon: 'invoices',
    title: 'Form payments',
    description: 'Card and ACH payments from any Gravity Form.'
  },
  {
    icon: 'recurring-billing',
    title: 'Recurring billing',
    description: 'Card-on-file and recurring payments through the iQ Pro+ Gateway.'
  },
  {
    icon: 'webhooks',
    title: 'Real-time updates',
    description: 'Orders and entries stay in sync as payments settle, void, or refund.'
  }
];

const IntegrationsECommerce = () => {
  const container = useRef();
  useGSAP(
    () => {
      gsap.fromTo(
        ['.hero-left', '.cols-span-2'],
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.inOut' }
      );
      ['.card', '.epic'].forEach((selector) =>
        gsap.from(selector, {
          duration: 1,
          opacity: 0,
          y: 50,
          stagger: 0.1,
          ease: 'power3.inOut',
          scrollTrigger: { trigger: selector, start: 'top bottom-=200', end: 'top bottom+=100' }
        })
      );
      gsap.fromTo(
        '.how > *',
        { opacity: 0, y: 100 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power2.in',
          stagger: 0.1,
          scrollTrigger: { trigger: '.how', start: 'top bottom-=200', end: 'top bottom+=100' }
        }
      );
    },
    { context: container }
  );

  return (
    <>
      <main ref={container}>
        <Header />
        <MobileNavigation />
        <section className="">
          <div className="row hero-row">
            <article className="hero-left">
              <h2 className="text-left eyebrow text-[#4452A3] dark:text-[#A5ABD9]">E-COMMERCE</h2>
              <h2 className="large">Payments built into your storefront.</h2>
              <p className="">
                Connect your storefront, checkout, and payment forms to your iQ Pro+ Gateway with
                plugins built for the platforms you already use.
              </p>
              <a href="#plugins" className="button button-primary has-arrow has-arrow-white">
                View plugins
              </a>
            </article>
            <figure className="cols-span-2">
              <img src={frontImg} alt="Shopping cart icon on a circuit board" />
            </figure>
          </div>
        </section>

        <section className="">
          <div className="grid grid-cols-1 gap-4 row sm:grid-cols-2">
            {capabilities.map(({ icon, title, description }) => (
              <article key={icon} className="card">
                <h2 className={`has-icon ${icon}`}>{title}</h2>
                <p className="light">{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="">
          <div
            className="row"
            style={{
              borderRadius: '25px',
              backgroundImage: `url(${background})`,
              backgroundSize: '200% 200%',
              backgroundPosition: 'bottom center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="p-4 mx-auto md:p-8 how">
              <h3 className="text-white large">Security that stays out of your way.</h3>
              <p className="text-white light">
                Hosted payment fields keep sensitive card data off your servers, while your checkout
                still looks and feels like your store.
              </p>
              <ul className="my-8 fancy-bullets white">
                <li>
                  <p className="text-white light">Reduces your PCI compliance scope.</p>
                </li>
                <li>
                  <p className="text-white light">Card numbers never touch your store or server.</p>
                </li>
                <li>
                  <p className="text-white light">
                    Full control over checkout design and user experience.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section
          id="plugins"
          className="grid grid-cols-1 gap-8 row my-20 scroll-mt-8 md:grid-cols-2 lg:grid-cols-3"
        >
          {plugins.map(({ name, slug, host, description }) => (
            <article
              key={slug}
              className="flex flex-col gap-4 p-6 border-2 border-primary rounded-[24px] shadow-[0px_3px_4px_0px_rgba(68,82,163,0.5)] epic"
            >
              <p className="mb-0 text-sm font-semibold uppercase tracking-wider text-[#4452A3] dark:text-[#A5ABD9]">
                {host}
              </p>
              <h2 className="mb-0 text-left">{name}</h2>
              <p className="flex-1 mb-0 light">{description}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/integrations-${slug}`} className="button button-primary">
                  Documentation
                </Link>
                <Link
                  to={`/integrations-${slug}-downloads`}
                  style={{ background: 'none' }}
                  className="button button-secondary dark:button-primary border-2 border-[#4452A3]"
                >
                  Downloads
                </Link>
              </div>
            </article>
          ))}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default IntegrationsECommerce;
